/* M2L Digital Studios - 3-D coverflow portfolio carousel.
   Auto-rotation, drag/swipe with momentum, click-to-center.

   Cards sit on a cylinder, ARC_STEP degrees apart, facing outward. The
   front card squarely faces the viewer; each card further round turns away
   and recedes, so perspective alone makes it narrower and shorter - width
   falling off faster than height, which is what gives the curve its
   rounded look. Nothing is scaled by hand.

   The track is pushed back by the radius so the front card lands at z = 0
   and renders at its natural size.

   Cards only cover an arc (N * ARC_STEP), not the full circle, so they
   fade to 0 at the ends of the arc - that hides the seam where a card
   wraps from one side to the other, and keeps the loop endless.

   GSAP is used only for ticker timing and value tweening; CSS transforms
   are written directly to guarantee the order:
     rotateY(angle) translateZ(radius)
   (turn the local frame to the card's slot, then push it out along that
   rotated frame - GSAP's pipeline reverses this, which collapses every
   card onto the same depth plane.) */
(function () {
  'use strict';

  var AUTO_DEG_SEC   = 14;    // degrees per second
  var DRAG_SENS      = 0.36;  // px of drag → degrees of rotation
  var RESUME_DELAY   = 2400;  // ms before auto-rotation restarts after interaction
  var MOMENTUM_MS    = 1300;  // momentum tween duration

  /* Cylinder geometry. ARC_STEP sets how tightly the cards are packed; the
     radius has to be large enough that neighbours don't intersect, which
     needs 2 * R * sin(ARC_STEP / 2) >= card width. */
  var ARC_STEP       = 19;    // degrees between adjacent cards
  var RADIUS_DESKTOP = 1100;  // px - card is 340 wide
  var RADIUS_MOBILE  = 650;   // px - card is 200 wide

  function isMobileVP() { return window.innerWidth < 760; }
  function getRadius()  { return isMobileVP() ? RADIUS_MOBILE : RADIUS_DESKTOP; }

  function initCarousel(section) {
    var scene = section.querySelector('.pc-scene');
    var track = section.querySelector('.pc-track');
    var cards = Array.prototype.slice.call(section.querySelectorAll('.pc-card'));
    if (!scene || !track || cards.length === 0) return;
    if (typeof gsap === 'undefined') return;

    var N = cards.length;
    var rotAngle = 0;           // track's current rotateY value (degrees)
    var isHovered = false;
    var isDragging = false;
    var hasDragged = false;     // distinguishes click vs drag release
    var dragStartX = 0;
    var dragStartAngle = 0;
    var dragHistory = [];       // [{x, t}] for velocity calculation
    var momentumAnim = null;
    var resumeTimer = null;
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---- Assign each card its slot on the loop ----
       The angle is only bookkeeping for the endless loop; the actual
       placement happens in refreshCards(). */
    var STEP = 360 / N;   // degrees of rotation per slot

    function positionCards() {
      cards.forEach(function (card, i) {
        card._baseAngle = i * STEP;
      });
    }

    positionCards();
    window.addEventListener('resize', function () {
      positionCards();
      refreshCards();
    });

    /* ---- Visual update each frame ---- */
    function relativeAngle(baseAngle) {
      var a = ((baseAngle + rotAngle) % 360 + 360) % 360;
      return a > 180 ? a - 360 : a; // -180 .. 180
    }

    function refreshCards() {
      var r = getRadius();

      /* Pull the cylinder back so its front face sits at z = 0, otherwise
         perspective would magnify the front card. */
      track.style.transform = 'translateZ(' + (-r) + 'px)';

      var half   = N / 2;                            // largest possible slot offset
      var fadeAt = Math.max(0.9, half - 0.9);        // start fading before the wrap seam

      cards.forEach(function (card) {
        var rel  = relativeAngle(card._baseAngle);
        var slot = rel / STEP;                       // 0 = front, ±1 = next card round
        var a    = Math.abs(slot);
        var theta = slot * ARC_STEP;                 // where the card sits on the cylinder

        /* Fade to nothing at the ends of the arc so the wrap is never seen */
        var opacity = a <= fadeAt
          ? 1
          : Math.max(0, 1 - (a - fadeAt) / (half - fadeAt));

        card.style.transform =
          'rotateY(' + theta + 'deg) ' +
          'translateZ(' + r + 'px)';
        card.style.opacity      = opacity;
        card.style.zIndex       = Math.round(100 - a * 10);
        card.style.pointerEvents = opacity < 0.05 ? 'none' : 'auto';
      });
    }

    refreshCards();

    /* ---- Auto-rotation ticker ---- */
    function onTick(time, deltaTime) {
      if (prefersReduced || isHovered || isDragging) return;
      rotAngle -= AUTO_DEG_SEC * (deltaTime / 1000);
      refreshCards();
    }
    gsap.ticker.add(onTick);

    /* ---- Hover pause ---- */
    scene.addEventListener('pointerenter', function () { isHovered = true; });
    scene.addEventListener('pointerleave', function () { isHovered = false; });

    /* ---- Velocity helpers ---- */
    function pushHistory(x) {
      var now = performance.now();
      dragHistory.push({ x: x, t: now });
      dragHistory = dragHistory.filter(function (h) { return now - h.t <= 80; });
    }

    function getVelocity() { // px/ms
      if (dragHistory.length < 2) return 0;
      var first = dragHistory[0];
      var last  = dragHistory[dragHistory.length - 1];
      var dt = last.t - first.t;
      return dt > 0 ? (last.x - first.x) / dt : 0;
    }

    /* ---- Drag / swipe ---- */
    function onDragStart(clientX) {
      if (momentumAnim) momentumAnim.kill();
      clearTimeout(resumeTimer);
      isDragging     = true;
      hasDragged     = false;
      isHovered      = true;
      dragStartX     = clientX;
      dragStartAngle = rotAngle;
      dragHistory    = [{ x: clientX, t: performance.now() }];
    }

    function onDragMove(clientX) {
      if (!isDragging) return;
      pushHistory(clientX);
      var totalDx = clientX - dragStartX;
      if (Math.abs(totalDx) > 4) hasDragged = true;
      rotAngle = dragStartAngle - totalDx * DRAG_SENS;
      refreshCards();
    }

    function onDragEnd() {
      if (!isDragging) return;
      isDragging = false;

      var vel = getVelocity(); // px/ms; negative = dragged left
      if (Math.abs(vel) > 0.08) {
        var momentumDeg = -vel * DRAG_SENS * 600;
        var endAngle    = rotAngle + momentumDeg;
        var proxy       = { val: rotAngle };
        momentumAnim = gsap.to(proxy, {
          val:      endAngle,
          duration: MOMENTUM_MS / 1000,
          ease:     'power3.out',
          onUpdate: function () { rotAngle = proxy.val; refreshCards(); },
        });
      }

      resumeTimer = setTimeout(function () { isHovered = false; }, RESUME_DELAY);
    }

    /* Mouse */
    scene.addEventListener('mousedown', function (e) {
      onDragStart(e.clientX);
      e.preventDefault();
    });
    document.addEventListener('mousemove', function (e) { onDragMove(e.clientX); });
    document.addEventListener('mouseup',   function ()  { onDragEnd(); });

    /* Touch */
    scene.addEventListener('touchstart', function (e) {
      onDragStart(e.touches[0].clientX);
    }, { passive: true });
    scene.addEventListener('touchmove', function (e) {
      onDragMove(e.touches[0].clientX);
    }, { passive: true });
    scene.addEventListener('touchend', function () { onDragEnd(); });

    /* ---- Click to center ---- */
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (hasDragged) return;
        var rel   = relativeAngle(card._baseAngle);
        var end   = rotAngle - rel;
        var proxy = { val: rotAngle };
        if (momentumAnim) momentumAnim.kill();
        gsap.to(proxy, {
          val:      end,
          duration: 0.72,
          ease:     'power3.inOut',
          onUpdate: function () { rotAngle = proxy.val; refreshCards(); },
        });
      });
    });
  }

  /* ---- Boot ---- */
  function boot() {
    document.querySelectorAll('[data-portfolio-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
